import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Bell,
  AlertTriangle,
  Clock,
  Trophy,
  UserPlus,
  Flame,
  Check,
  X,
  MessageCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Settings2,
  Sparkles,
  Send,
  Zap,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const ALERT_ICONS = {
  missed_workouts: AlertTriangle,
  streak_break: Flame,
  checkin_overdue: Clock,
  milestone: Trophy,
  new_signup: UserPlus
};

const ALERT_COLORS = {
  missed_workouts: 'text-red-500 bg-red-500/10',
  streak_break: 'text-orange-500 bg-orange-500/10',
  checkin_overdue: 'text-yellow-500 bg-yellow-500/10',
  milestone: 'text-green-500 bg-green-500/10',
  new_signup: 'text-blue-500 bg-blue-500/10'
};

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

export default function TrainerAlertsPanel({ onSendMessage }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('alerts'); // alerts, auto, digest
  const [thresholds, setThresholds] = useState({
    missed_workouts: 3,
    streak_break_min: 5,
    checkin_overdue_days: 7
  });
  const [autoSettings, setAutoSettings] = useState({
    enabled: false,
    use_ai: true,
    delay_minutes: 30,
    alert_types: {}
  });
  const [digest, setDigest] = useState(null);
  const [generatingMessage, setGeneratingMessage] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, countRes] = await Promise.all([
        axios.get(`${API}/alerts?status=unread&limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/alerts/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Sort by priority
      const sortedAlerts = (alertsRes.data.alerts || []).sort((a, b) => 
        (PRIORITY_ORDER[a.priority] || 2) - (PRIORITY_ORDER[b.priority] || 2)
      );
      
      setAlerts(sortedAlerts);
      setUnreadCount(countRes.data.total || 0);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchThresholds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts/thresholds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThresholds(response.data);
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    }
  }, [token]);

  const fetchAutoSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts/auto-message/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoSettings(response.data);
    } catch (error) {
      console.error('Error fetching auto settings:', error);
    }
  }, [token]);

  const fetchDigest = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts/digest?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDigest(response.data);
    } catch (error) {
      console.error('Error fetching digest:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
    fetchThresholds();
    fetchAutoSettings();
    fetchDigest();
  }, [fetchAlerts, fetchThresholds, fetchAutoSettings, fetchDigest]);

  const scanForAlerts = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API}/alerts/scan`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Scan complete: ${response.data.alerts_created} new alerts`);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to scan for alerts');
    } finally {
      setScanning(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/mark-read/${alertId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/resolve/${alertId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Alert resolved');
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/alerts/mark-all-read`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts([]);
      setUnreadCount(0);
      toast.success('All alerts marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const updateThresholds = async () => {
    try {
      await axios.put(`${API}/alerts/thresholds`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: thresholds
      });
      toast.success('Alert thresholds updated');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update thresholds');
    }
  };

  const updateAutoSettings = async () => {
    try {
      const params = {
        enabled: autoSettings.enabled,
        use_ai: autoSettings.use_ai,
        delay_minutes: autoSettings.delay_minutes,
        ...autoSettings.alert_types
      };
      await axios.put(`${API}/alerts/auto-message/settings`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      toast.success('Auto-message settings updated');
    } catch (error) {
      toast.error('Failed to update auto-message settings');
    }
  };

  const generateAutoMessage = async (alertId, send = false) => {
    setGeneratingMessage(alertId);
    try {
      const response = await axios.post(
        `${API}/alerts/auto-message/generate/${alertId}?send=${send}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (send) {
        toast.success(`AI message sent to ${response.data.client_name}!`);
        // Remove from alerts list
        setAlerts(prev => prev.filter(a => a.id !== alertId));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        // Show preview
        toast.success(
          <div>
            <p className="font-medium">AI Message Preview:</p>
            <p className="text-sm mt-1 italic">"{response.data.message}"</p>
          </div>,
          { duration: 8000 }
        );
      }
    } catch (error) {
      toast.error('Failed to generate message');
    } finally {
      setGeneratingMessage(null);
    }
  };

  const handleSendMessage = (clientId, clientName) => {
    if (onSendMessage) {
      onSendMessage(clientId, clientName);
    } else {
      // Navigate to messages page
      window.location.href = `/messages?to=${clientId}`;
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="card-tactical">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="trainer-alerts-panel" className="card-tactical">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 font-heading text-lg text-white uppercase hover:text-primary transition-colors"
        >
          <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-primary' : ''}`} />
          CLIENT ALERTS
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">
              {unreadCount}
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="text-gray-400 hover:text-white"
          >
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={scanForAlerts}
            disabled={scanning}
            className="border-gray-600"
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="mb-4 p-4 bg-secondary/50 rounded-sm space-y-3">
          <h4 className="text-sm font-medium text-white">Alert Thresholds</h4>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-400">Missed Workouts</label>
              <input
                type="number"
                min="1"
                max="10"
                value={thresholds.missed_workouts}
                onChange={(e) => setThresholds(prev => ({ ...prev, missed_workouts: parseInt(e.target.value) }))}
                className="w-full mt-1 bg-secondary text-white rounded-sm px-2 py-1 text-sm border-0"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Streak Break Min</label>
              <input
                type="number"
                min="3"
                max="30"
                value={thresholds.streak_break_min}
                onChange={(e) => setThresholds(prev => ({ ...prev, streak_break_min: parseInt(e.target.value) }))}
                className="w-full mt-1 bg-secondary text-white rounded-sm px-2 py-1 text-sm border-0"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Check-in Overdue (days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={thresholds.checkin_overdue_days}
                onChange={(e) => setThresholds(prev => ({ ...prev, checkin_overdue_days: parseInt(e.target.value) }))}
                className="w-full mt-1 bg-secondary text-white rounded-sm px-2 py-1 text-sm border-0"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={updateThresholds} className="bg-primary hover:bg-red-700">
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Alerts List */}
      {expanded && (
        <>
          {alerts.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-10 h-10 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500">No pending alerts</p>
              <p className="text-xs text-gray-600 mt-1">Click scan to check for new alerts</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {alerts.map((alert) => {
                const Icon = ALERT_ICONS[alert.alert_type] || Bell;
                const colorClass = ALERT_COLORS[alert.alert_type] || 'text-gray-500 bg-gray-500/10';
                
                return (
                  <div
                    key={alert.id}
                    data-testid={`alert-${alert.id}`}
                    className={`p-3 rounded-sm border transition-all ${
                      alert.priority === 'high' ? 'border-red-500/30 bg-red-500/5' :
                      alert.priority === 'medium' ? 'border-yellow-500/30 bg-yellow-500/5' :
                      'border-border bg-secondary/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-sm ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-medium text-white truncate">
                            {alert.title}
                          </h4>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {getTimeAgo(alert.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                          {alert.message}
                        </p>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => generateAutoMessage(alert.id, true)}
                            disabled={generatingMessage === alert.id}
                            className="h-7 px-2 text-xs text-purple-400 hover:text-purple-300"
                          >
                            {generatingMessage === alert.id ? (
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3 mr-1" />
                            )}
                            AI Message
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendMessage(alert.client_id, alert.client_name)}
                            className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300"
                          >
                            <MessageCircle className="w-3 h-3 mr-1" />
                            Custom
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resolveAlert(alert.id)}
                            className="h-7 px-2 text-xs text-green-400 hover:text-green-300"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Resolve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markAsRead(alert.id)}
                            className="h-7 px-2 text-xs text-gray-400 hover:text-gray-300"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {alerts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
              <span className="text-xs text-gray-500">{alerts.length} unread alerts</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllRead}
                className="text-xs text-gray-400 hover:text-white"
              >
                Mark all as read
              </Button>
            </div>
          )}

          {/* Digest Summary */}
          {digest && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Weekly Summary
                </h4>
                <div className={`flex items-center gap-1 text-xs ${
                  digest.trend.direction === 'up' ? 'text-red-400' :
                  digest.trend.direction === 'down' ? 'text-green-400' :
                  'text-gray-400'
                }`}>
                  {digest.trend.direction === 'up' ? <TrendingUp className="w-3 h-3" /> :
                   digest.trend.direction === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
                  {digest.trend.percentage > 0 ? '+' : ''}{digest.trend.percentage}%
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 bg-secondary/50 rounded-sm">
                  <div className="text-lg font-mono text-white">{digest.total_alerts}</div>
                  <div className="text-xs text-gray-500">Total Alerts</div>
                </div>
                <div className="text-center p-2 bg-red-500/10 rounded-sm">
                  <div className="text-lg font-mono text-red-400">{digest.high_priority_count}</div>
                  <div className="text-xs text-gray-500">High Priority</div>
                </div>
                <div className="text-center p-2 bg-yellow-500/10 rounded-sm">
                  <div className="text-lg font-mono text-yellow-400">{digest.clients_needing_attention}</div>
                  <div className="text-xs text-gray-500">Need Attention</div>
                </div>
              </div>
            </div>
          )}

          {/* Auto-Message Settings */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Auto-Messages
              </h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoSettings.enabled}
                  onChange={(e) => {
                    const newSettings = { ...autoSettings, enabled: e.target.checked };
                    setAutoSettings(newSettings);
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary focus:ring-primary"
                />
                <span className="text-xs text-gray-400">
                  {autoSettings.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
            
            {autoSettings.enabled && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-secondary/30 rounded-sm">
                  <span className="text-xs text-gray-400">Use AI for messages</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoSettings.use_ai}
                      onChange={(e) => setAutoSettings(prev => ({ ...prev, use_ai: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 bg-secondary text-primary focus:ring-primary"
                    />
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-2 bg-secondary/30 rounded-sm">
                  <span className="text-xs text-gray-400">Delay (minutes)</span>
                  <input
                    type="number"
                    min="0"
                    max="1440"
                    value={autoSettings.delay_minutes}
                    onChange={(e) => setAutoSettings(prev => ({ ...prev, delay_minutes: parseInt(e.target.value) || 0 }))}
                    className="w-16 bg-secondary text-white rounded-sm px-2 py-1 text-xs border-0 text-right"
                  />
                </div>
                
                <Button
                  size="sm"
                  onClick={updateAutoSettings}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Send className="w-3 h-3 mr-2" />
                  Save Auto-Message Settings
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
